import apiClient from '@/api/client'
import type { AvatarSignedUrl, CurrentUserResponse } from '@/types/auth.types'
import type { AuthUserListItem } from '@/types/team.types'

export interface SelfUpdatePayload {
  username?: string | null
}

interface PaginatedAuthUsers {
  items: AuthUserListItem[]
}

export const authUserService = {
  /**
   * Roster of internal users visible to the caller. The backend scopes the
   * rows by `leader_id` (admin → everyone, manager → their subtree). 500 is
   * the backend page-size cap — the internal team is small.
   */
  listAuthUsers(): Promise<AuthUserListItem[]> {
    return apiClient
      .get<PaginatedAuthUsers>('/auth-user/', { params: { page_size: 500 } })
      .then((r) => r.data.items ?? [])
  },

  /**
   * Internal roster (admin/manager/user, no portal partners) for the admin
   * Users page. `URLSearchParams` because FastAPI reads a repeated
   * `access_level=` key, not axios' default `access_level[]=` form.
   */
  listInternalUsers(): Promise<AuthUserListItem[]> {
    const params = new URLSearchParams([
      ['access_level', 'access_level.admin'],
      ['access_level', 'access_level.manager'],
      ['access_level', 'access_level.user'],
      ['page_size', '100'],
      ['sort_by', 'email'],
    ])
    return apiClient
      .get<PaginatedAuthUsers>('/auth-user/', { params })
      .then((r) => r.data.items ?? [])
  },

  /** Admin-only: grant or revoke the leader flag (PMS dashboard access). */
  setLeader(userId: number, isLeader: boolean): Promise<CurrentUserResponse> {
    return apiClient
      .patch<CurrentUserResponse>(`/auth-user/${userId}`, { is_leader: isLeader })
      .then((r) => r.data)
  },

  getMe(): Promise<CurrentUserResponse> {
    return apiClient.get<CurrentUserResponse>('/auth-user/me').then((r) => r.data)
  },

  patchMe(payload: SelfUpdatePayload): Promise<CurrentUserResponse> {
    return apiClient.patch<CurrentUserResponse>('/auth-user/me', payload).then((r) => r.data)
  },

  uploadMyAvatar(file: File): Promise<void> {
    const form = new FormData()
    form.append('file', file)
    return apiClient
      .post('/auth-user/me/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => undefined)
  },

  getMyAvatarUrl(): Promise<AvatarSignedUrl | null> {
    return apiClient
      .get<AvatarSignedUrl>('/auth-user/me/avatar-url')
      .then((r) => r.data)
      .catch((e: { response?: { status?: number } }) => {
        if (e.response?.status === 404) return null
        throw e
      })
  },

  deleteMyAvatar(): Promise<void> {
    return apiClient.delete('/auth-user/me/avatar').then(() => undefined)
  },
}
